import {
    useCallback, useContext, useEffect, useMemo, useRef,
} from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import cls from 'classnames';
import { isEmpty } from 'lodash-es';
import { faEdit, faPlus, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TipAlert } from '../TipAlert';
import {
    FlexBetweenRow,
    FlexEndRow, FormContext, FormLabel, SmallButton,
} from './FormElements';

const animatedComponents = makeAnimated();

const isPrimitive = (value) => value !== Object(value);
const defaultOnChange = () => { };

export const ReactSelectStyles = {
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    control: (base) => ({
        ...base,
        // height: 35, //Was causing to not expand with multi
        minHeight: 35,
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        '&:hover': { borderColor: '#ccc' }, // border style on hover
        border: '1px solid #ccc', // default border color
        boxShadow: 'none', // no box-shadow
    }),
    dropdownIndicator: (base) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
    }),
    clearIndicator: (base) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
    }),
};

export const CompactSelectStyles = {
    ...ReactSelectStyles,
    control: (base) => ({
        ...base,
        // height: 28, //Was causing to not expand with multi
        minHeight: 28,
        minMenuHeight: 100,
        menuHeight: 200,
        maxMenuHeight: 200,
        fontSize: '0.74375rem',
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        '&:hover': { borderColor: '#ccc' }, // border style on hover
        border: '1px solid #ccc', // default border color
        boxShadow: 'none', // no box-shadow
    }),
};

export const SuperCompactSelectStyles = {
    ...ReactSelectStyles,
    control: (base) => ({
        ...base,
        height: 20, // Was causing to not expand with multi
        minHeight: 20,
        minMenuHeight: 100,
        menuHeight: 200,
        maxMenuHeight: 200,
        fontSize: '0.74375rem',
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        '&:hover': { borderColor: '#ccc' }, // border style on hover
        border: '1px solid #ccc', // default border color
        boxShadow: 'none', // no box-shadow
    }),
};

export const ValidatedSelect = (props) => {
    const context = useContext(FormContext);
    const inputRef = useRef(null);
    const prevValueRef = useRef(null);

    const updateReactSelectStyles = useCallback(() => {
        // Don't display validation messages until the first submission attempt of the form.
        // Key in on Bootstrap validation classes to display red-border and/or validation
        // messages under the dropdown.
        const input = inputRef.current;

        if (!input) {
            return;
        }

        const form = input.closest('form');

        if (!form) {
            return;
        }

        const form_was_validated = ((form ?? {}).classList ?? []).contains(
            'was-validated',
        );

        const form_group = input.closest('.form-group');
        const select = form_group.querySelector('.react-select');
        const errorLabel = form_group.querySelector('.invalid-feedback');

        // Show-hide validation messages
        if (form_was_validated) {
            if (errorLabel) {
                errorLabel.hidden = input.validity.valid;
            }

            // Add form-control styles
            if (input && input.validity.valid) {
                select.classList.remove('is-invalid');
                select.classList.add('is-valid');
            } else {
                select.classList.add('is-invalid');
                select.classList.remove('is-valid');
            }
        } else if (input && input.validity?.valid) {
            select.classList.remove('is-invalid');
        }
    }, []);

    const {
        value,
        onChange = defaultOnChange,
        options = [],
        valueField = 'value',
        asPrimitive = false,
        asObject = false,
        formGroupClassName = '',
        className = '',
        helperText,
        formGroupHidden = false,
        label = null,
        required = false,
        id: fieldId,
        name,
        error,
        helpMessage = '',
        onSelectAll = null,
        showAddButton = false,
        onAddButtonClick = defaultOnChange,
        addButtonLabel = 'Add',
        addButtonIcon = faPlus,
        showEditButton = false,
        onEditButtonClick = defaultOnChange,
        editButtonLabel = 'Edit',
        editButtonIcon = faEdit,
        showViewButton = false,
        onViewButtonClick = defaultOnChange,
        viewButtonLabel = 'View',
        viewButtonIcon = faEye,
        disabled = false,
        isDisabled = false,
        component: Component = Select,
        isMulti = false,
        hideClear = false,
        form = null,
        TipComponentProps = null,
        loadOptions = null,
        ...restProps
    } = props;

    // Map both of these to the same prop
    const fieldDisabled = disabled || isDisabled;

    const [fieldValue, fieldOnChange, formId] = useMemo(() => {
        let calculatedValue = value;
        let calculatedOnChange = onChange;
        let calculatedFormId = form;

        if (context && context.values) {
            calculatedFormId = context.formId;

            calculatedOnChange = (e, f) => {
                let changeVal = e;

                if (asPrimitive) {
                    if (Array.isArray(e)) {
                        changeVal = e.map((val) => {
                            if (!isPrimitive(val) && val) {
                                return val[valueField];
                            }

                            return val;
                        });
                    } else if (!isPrimitive(e) && e) {
                        changeVal = e[valueField];
                    }
                } else if (asObject) {
                    changeVal = e;
                }

                const fieldData = {
                    ...f || {},
                    asObject,
                    valueField,
                };

                onChange(changeVal, fieldData);
                context.onChange(changeVal, fieldData);
            };

            if (isMulti) {
                calculatedValue = context.values[name || fieldId];

                // Some multi fields are comma-separated values
                // These fields need to be converted to an array
                if (!isEmpty(calculatedValue) && typeof calculatedValue === 'string') {
                    calculatedValue = calculatedValue.split(',').map((val) => {
                        if (isPrimitive(val)) {
                            return options.find((opt) => opt[valueField] == val) || val;
                        }

                        return val;
                    });

                    // Save the field back to the context
                    // We do this because the field structure itself had to be transformed
                    context.setFieldValue(name || fieldId, calculatedValue);
                } else if (Array.isArray(calculatedValue)) {
                    calculatedValue = calculatedValue.map((val) => {
                        if (isPrimitive(val)) {
                            return options.find((opt) => opt[valueField] == val) || val;
                        }

                        return val;
                    });
                }
            } else {
                if ((!options || !options.length) && loadOptions) {
                    // For async fields, the value gets injected straight into the context
                    calculatedValue = context.values[name || fieldId];
                } else {
                    calculatedValue = options?.find((option) => option[valueField] === context.values[name || fieldId]);
                }
            }
        }

        return [calculatedValue, calculatedOnChange, calculatedFormId];
    }, [asPrimitive, fieldId, form, onChange, options, value, valueField, context, isMulti, name, asObject, loadOptions]);

    useEffect(() => {
        if (prevValueRef.current !== fieldValue) {
            prevValueRef.current = fieldValue;

            updateReactSelectStyles();
        }
    }, [fieldValue, updateReactSelectStyles]);

    const hasValue = isMulti
        ? !!(fieldValue ?? '').length
        : !!fieldValue;

    return (
        <div
            hidden={formGroupHidden}
            className={cls('form-group', 'form-group-input', !label && 'mb-0', formGroupClassName)}
        >
            {Boolean(label) && (
                <FlexBetweenRow className="align-items-center">
                    <FormLabel
                        htmlFor={fieldId}
                        text={label}
                        required={required}
                        helpMessage={helpMessage}
                    />

                    {Boolean(onSelectAll) && (
                        <FlexEndRow>
                            <SmallButton
                                color="link"
                                className="site-link w-auto"
                                onClick={onSelectAll}
                            >
                                Select All
                            </SmallButton>
                        </FlexEndRow>
                    )}

                    {(Boolean(showAddButton) || Boolean(showEditButton) && !fieldDisabled) && (
                        <FlexEndRow>
                            {Boolean(showEditButton) && ( // We don't check for fieldDisabled to allow for View Mode override
                                <SmallButton
                                    color="link"
                                    className="site-link w-auto site-button-small mr-1 secondary-action"
                                    onClick={onEditButtonClick}
                                >
                                    <FontAwesomeIcon
                                        icon={editButtonIcon}
                                        className="mr-1"
                                    />

                                    {editButtonLabel}
                                </SmallButton>
                            )}

                            {Boolean(showAddButton) && (
                                <SmallButton
                                    color="link"
                                    className="site-link w-auto site-button-small secondary-action"
                                    onClick={onAddButtonClick}
                                >
                                    <FontAwesomeIcon
                                        icon={addButtonIcon}
                                        className="mr-1"
                                    />

                                    {addButtonLabel}
                                </SmallButton>
                            )}
                        </FlexEndRow>
                    )}

                    {fieldDisabled && showViewButton && (
                        <SmallButton
                            color="link"
                            className="site-link w-auto"
                            onClick={onViewButtonClick}
                        >
                            <FontAwesomeIcon
                                icon={viewButtonIcon}
                                className="mr-1"
                            />

                            {viewButtonLabel}
                        </SmallButton>
                    )}
                </FlexBetweenRow>
            )}

            {Boolean(TipComponentProps) && (
                <TipAlert
                    {...TipComponentProps}
                />
            )}

            <Component
                styles={ReactSelectStyles}
                isClearable={!hideClear}
                components={animatedComponents}
                className={cls(
                    'react-select',
                    'required',
                    className,
                )}
                classNamePrefix="react-select"
                isDisabled={fieldDisabled}
                loadOptions={loadOptions}
                value={fieldValue}
                onChange={fieldOnChange}
                options={options}
                id={fieldId}
                name={name}
                isMulti={isMulti}
                form={formId}
                {...restProps}
            />

            <input
                ref={inputRef}
                type="text"
                className="react-select-validated-input"
                tabIndex={-1}
                autoComplete="off"
                style={{
                    opacity: 0,
                    height: 0,
                    margin: 0,
                    padding: 0,
                    border: 'none',
                }}
                value={hasValue ? JSON.stringify(fieldValue) : ''}
                required={required}
                pattern="\[.+?\]|\{.+?\}"
                onChange={() => { }}
                form={formId}
            />

            {Boolean(error) && !fieldDisabled && (
                <small className="invalid-feedback text-danger">
                    {error}
                </small>
            )}

            {Boolean(helperText) && (
                <small className="invalid-feedback helper-text text-muted">
                    {helperText}
                </small>
            )}
        </div>
    );
};

export default ValidatedSelect;
